import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import {
  HiOutlineUsers,
  HiOutlineSearch,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineClock,
  HiOutlineShieldCheck,
  HiOutlineBan,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'

function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Read initial filter from URL (e.g., /admin/users?is_approved=false)
  const getInitialApprovalFilter = () => {
    const isApproved = searchParams.get('is_approved')
    if (isApproved === 'false') return 'pending'
    if (isApproved === 'true') return 'approved'
    return ''
  }

  const [users, setUsers] = useState([])
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [teamFilter, setTeamFilter] = useState('')
  const [approvalFilter, setApprovalFilter] = useState(getInitialApprovalFilter)  // '' | 'pending' | 'approved'
  const [showModal, setShowModal] = useState(false)
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [userToDelete, setUserToDelete] = useState(null)
  const [newTeamName, setNewTeamName] = useState('')
  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState({
    employee_id: '',
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'sales',
    team_id: '',
    is_active: true,
    is_approved: true,  // Admin-created users are auto-approved
  })
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 })

  useEffect(() => {
    loadUsers()
    loadTeams()
  }, [searchQuery, roleFilter, teamFilter, approvalFilter, pagination.page])

  // Sync URL with approvalFilter
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (approvalFilter === 'pending') {
      params.set('is_approved', 'false')
    } else if (approvalFilter === 'approved') {
      params.set('is_approved', 'true')
    } else {
      params.delete('is_approved')
    }
    setSearchParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalFilter, setSearchParams])

  const loadUsers = async () => {
    setLoading(true)
    try {
      const response = await api.get('/admin/users', {
        params: {
          page: pagination.page,
          per_page: 20,
          search: searchQuery || undefined,
          role: roleFilter || undefined,
          team_id: teamFilter || undefined,
          is_approved: approvalFilter === 'pending' ? 'false' : approvalFilter === 'approved' ? 'true' : undefined,
        }
      })
      if (response.data.status === 'success') {
        setUsers(response.data.data)
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages,
        }))
      }
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Không thể tải danh sách người dùng')
    } finally {
      setLoading(false)
    }
  }

  const loadTeams = async () => {
    try {
      const response = await api.get('/admin/teams')
      if (response.data.status === 'success') {
        setTeams(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load teams:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const payload = { ...formData }
      // Convert empty string team_id to null
      if (!payload.team_id) payload.team_id = null

      if (editingUser) {
        await api.put(`/admin/users/${editingUser.id}`, payload)
        toast.success('Đã cập nhật người dùng')
      } else {
        await api.post('/admin/users', payload)
        toast.success('Đã tạo người dùng mới')
      }
      setShowModal(false)
      resetForm()
      loadUsers()
    } catch (error) {
      console.error('Failed to save user:', error)
      toast.error(error.response?.data?.message || 'Lỗi khi lưu')
    }
  }

  const handleEdit = (user) => {
    setEditingUser(user)
    setFormData({
      employee_id: user.employee_id,
      email: user.email || '',
      password: '',
      full_name: user.full_name,
      phone: user.phone || '',
      role: user.role,
      team_id: user.team_id || '',
      is_active: user.is_active,
      is_approved: user.is_approved,
    })
    setShowModal(true)
  }

  const handleDeleteClick = (user) => {
    // Admin accounts cannot be deleted
    if (user.role === 'admin') {
      toast.error('Không thể xóa tài khoản admin')
      return
    }
    setUserToDelete(user)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return

    try {
      await api.delete(`/admin/users/${userToDelete.id}`)
      toast.success('Đã xóa người dùng thành công')
      loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
      toast.error(error.response?.data?.message || 'Không thể xóa người dùng')
    } finally {
      setShowDeleteModal(false)
      setUserToDelete(null)
    }
  }

  const resetForm = () => {
    setEditingUser(null)
    setFormData({
      employee_id: '',
      email: '',
      password: '',
      full_name: '',
      phone: '',
      role: 'sales',
      team_id: '',
      is_active: true,
      is_approved: true,  // Admin-created users are auto-approved
    })
  }

  const handleCreateTeam = async (e) => {
    e.preventDefault()
    if (!newTeamName.trim()) return

    try {
      const response = await api.post('/admin/teams', { name: newTeamName.trim() })
      if (response.data.status === 'success') {
        toast.success('Đã tạo phòng ban mới')
        const newTeam = response.data.data
        setTeams(prev => [...prev, newTeam])
        setFormData(prev => ({ ...prev, team_id: newTeam.id.toString() }))
        setShowTeamModal(false)
        setNewTeamName('')
      }
    } catch (error) {
      console.error('Failed to create team:', error)
      toast.error(error.response?.data?.message || 'Không thể tạo phòng ban')
    }
  }

  const handleApprove = async (user) => {
    try {
      await api.post(`/admin/users/${user.id}/approve`)
      toast.success(`Đã phê duyệt ${user.full_name}`)
      loadUsers()
    } catch (error) {
      console.error('Failed to approve user:', error)
      toast.error(error.response?.data?.message || 'Không thể phê duyệt')
    }
  }

  const handleReject = async (user) => {
    if (!confirm(`Từ chối tài khoản "${user.full_name}"? Tài khoản sẽ bị vô hiệu hóa.`)) return

    try {
      await api.post(`/admin/users/${user.id}/reject`)
      toast.success(`Đã từ chối ${user.full_name}`)
      loadUsers()
    } catch (error) {
      console.error('Failed to reject user:', error)
      toast.error(error.response?.data?.message || 'Không thể từ chối')
    }
  }

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      leader: 'bg-purple-100 text-purple-700',
      sales: 'bg-blue-100 text-blue-700',
    }
    const labels = {
      admin: 'Admin',
      leader: 'Leader',
      sales: 'Sales',
    }
    return (
      <span className={clsx('px-2 py-1 rounded text-xs font-medium', styles[role])}>
        {labels[role] || role}
      </span>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HiOutlineUsers className="w-6 h-6 mr-2 text-blue-500" />
            Quản lý người dùng
          </h1>
          <p className="text-gray-500 mt-1">
            Tổng cộng {pagination.total} người dùng
          </p>
        </div>
        <button
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          className="btn-primary"
        >
          <HiOutlinePlus className="w-5 h-5 mr-2" />
          Thêm người dùng
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="grid sm:grid-cols-4 gap-4">
          <div className="relative">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Tìm kiếm..."
              className="input pl-10"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="input"
          >
            <option value="">Tất cả vai trò</option>
            <option value="admin">Admin</option>
            <option value="leader">Leader</option>
            <option value="sales">Sales</option>
          </select>
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="input"
          >
            <option value="">Tất cả phòng ban</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <select
            value={approvalFilter}
            onChange={(e) => setApprovalFilter(e.target.value)}
            className={clsx(
              'input',
              approvalFilter === 'pending' && 'border-amber-500 bg-amber-50'
            )}
          >
            <option value="">Tất cả trạng thái</option>
            <option value="pending">Chờ duyệt</option>
            <option value="approved">Đã duyệt</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  Người dùng
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  Liên hệ
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  Vai trò
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  Phòng ban
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                  Trạng thái
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Không có người dùng nào
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt={user.full_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {user.full_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.full_name}</p>
                          <p className="text-xs text-gray-500">#{user.employee_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        {user.email && <p className="text-gray-600">{user.email}</p>}
                        {user.phone && <p className="text-gray-500">{user.phone}</p>}
                        {!user.email && !user.phone && <span className="text-gray-400">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.team_name || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {user.is_active ? (
                          <span className="inline-flex items-center text-green-600 text-sm">
                            <HiOutlineCheck className="w-4 h-4 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-gray-400 text-sm">
                            <HiOutlineX className="w-4 h-4 mr-1" />
                            Inactive
                          </span>
                        )}
                        {!user.is_approved && user.is_active && (
                          <span className="inline-flex items-center text-amber-600 text-xs bg-amber-50 px-2 py-0.5 rounded-full w-fit">
                            <HiOutlineClock className="w-3 h-3 mr-1" />
                            Chờ duyệt
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Approve/Reject buttons for pending users */}
                        {!user.is_approved && user.is_active && (
                          <>
                            <button
                              onClick={() => handleApprove(user)}
                              className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                              title="Phê duyệt"
                            >
                              <HiOutlineShieldCheck className="w-5 h-5 text-green-600" />
                            </button>
                            <button
                              onClick={() => handleReject(user)}
                              className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              title="Từ chối"
                            >
                              <HiOutlineBan className="w-5 h-5 text-red-500" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Sửa"
                        >
                          <HiOutlinePencil className="w-5 h-5 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(user)}
                          className={clsx(
                            "p-2 rounded-lg transition-colors",
                            user.role === 'admin'
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-red-50"
                          )}
                          title={user.role === 'admin' ? 'Không thể xóa admin' : 'Xóa'}
                        >
                          <HiOutlineTrash className={clsx(
                            "w-5 h-5",
                            user.role === 'admin' ? "text-gray-400" : "text-red-500"
                          )} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="p-4 border-t border-gray-100 flex justify-center gap-2">
            {Array.from({ length: pagination.pages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                className={clsx(
                  'px-3 py-1 rounded-lg text-sm font-medium',
                  pagination.page === i + 1
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingUser ? 'Sửa người dùng' : 'Thêm người dùng mới'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mã nhân viên
                  </label>
                  <input
                    type="text"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="input"
                    required
                    disabled={!!editingUser}
                    placeholder="VD: NV001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Họ tên
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="input"
                    required
                    placeholder="Nguyễn Văn A"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Số điện thoại
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input"
                    placeholder="0901234567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editingUser ? 'Mật khẩu mới (để trống nếu không đổi)' : 'Mật khẩu'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input"
                  required={!editingUser}
                  placeholder="••••••••"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vai trò
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="input"
                  >
                    <option value="sales">Sales</option>
                    <option value="leader">Leader</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phòng ban
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={formData.team_id}
                      onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                      className="input flex-1"
                    >
                      <option value="">Không có phòng ban</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowTeamModal(true)}
                      className="btn-outline px-3"
                      title="Tạo phòng ban mới"
                    >
                      <HiOutlinePlus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">
                    Tài khoản hoạt động
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_approved"
                    checked={formData.is_approved}
                    onChange={(e) => setFormData({ ...formData, is_approved: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="is_approved" className="text-sm text-gray-700">
                    Đã phê duyệt
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-outline flex-1"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingUser ? 'Cập nhật' : 'Tạo mới'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Modal - Tạo phòng ban mới */}
      {showTeamModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Tạo phòng ban mới
              </h2>
            </div>
            <form onSubmit={handleCreateTeam} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên phòng ban
                </label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  className="input"
                  required
                  autoFocus
                  placeholder="VD: Phòng Kinh doanh 1"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false)
                    setNewTeamName('')
                  }}
                  className="btn-outline flex-1"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Tạo mới
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-red-600 flex items-center">
                <HiOutlineTrash className="w-5 h-5 mr-2" />
                Xác nhận xóa người dùng
              </h2>
            </div>
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 text-sm font-medium mb-2">
                  Hành động này không thể hoàn tác!
                </p>
                <p className="text-red-700 text-sm">
                  Tất cả dữ liệu liên quan đến người dùng này sẽ bị xóa vĩnh viễn khỏi hệ thống.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-1">Người dùng sẽ bị xóa:</p>
                <p className="font-medium text-gray-900">{userToDelete.full_name}</p>
                <p className="text-sm text-gray-500">#{userToDelete.employee_id}</p>
                {userToDelete.email && (
                  <p className="text-sm text-gray-500">{userToDelete.email}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false)
                    setUserToDelete(null)
                  }}
                  className="btn-outline flex-1"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Xóa vĩnh viễn
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
